import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Table,
  Row,
  Col
} from "react-bootstrap";
import "react-datepicker/dist/react-datepicker.css";
import { normalSort } from "../Utility/sort";
import { ChartScreen, SearchAndDownload, TimeControl } from "../components";
import * as XLSX from "xlsx";
import { stringYMDHMS3 } from "../Utility/dateTime";
import { colors } from "../configs";
import { handleDigit, searchFilterFunction } from "../Utility/function";

const { backgroundColor, white } = colors;

const arrangeOptions = [
  { id:1, name:'ยอดขายสุทธิ จาก มาก ไป น้อย'},
  { id:2, name:'ยอดขายสุทธิ จาก น้อย ไป มาก'},
  { id:3, name:'ต้นทุน จาก มาก ไป น้อย'},
  { id:4, name:'ต้นทุน จาก น้อย ไป มาก'},
  { id:5, name:'กำไร จาก มาก ไป น้อย'},
  { id:6, name:'กำไร จาก น้อย ไป มาก'},
  { id:7, name:'อัตรากำไร จาก มาก ไป น้อย'},
  { id:8, name:'อัตรากำไร จาก น้อย ไป มาก'},
  { id:9, name:'จำนวนที่ขาย จาก มาก ไป น้อย'},
  { id:10, name:'จำนวนที่ขาย จาก น้อย ไป มาก'},
]

function ReportByCategory() {
    const { selectedBill, startDate, endDate } = useSelector((state)=> state.bill);
    const { shop:{ smartCategory = [] } } = useSelector(state=>state.shop)
      const [search, setSearch] = useState('');

      const { masterDisplay, totalSale, totalCost, totalDiscount, totalQty, totalProfit, totalPrice, totalProfitPercent } = useMemo(()=>{
        const firstLevel = new Map((smartCategory.find(a=>a.level === 1)?.value || []).map(category => [category.id, category.name]));

        let newCategory = []
        let totalSale = 0;
        let totalProfit = 0;
        let totalCost = 0;
        let totalDiscount = 0;
        let totalQty = 0;
        let thisPrice = 0
        let masterProducts = selectedBill.flatMap(a=>a.product)
        if(search){
          masterProducts = searchFilterFunction(masterProducts,search,'name')
        } 
        masterProducts.forEach(item=>{
          const { qty, totalPrice, net, totalCost:cost, category = [] } = item;
          if(category.length>0){
            let findCategory = newCategory.find(a=>a.id === category[0]);
            if(findCategory){
                findCategory.qty += qty
                findCategory.totalPrice += Number(totalPrice)
                findCategory.net += Number(net)
                findCategory.totalCost += Number(cost)
            } else {
                const name = firstLevel.get(category[0]) || 'ไม่พบหมวดหมู่'
                newCategory.push({
                    id:category[0], name, qty, totalPrice:Number(totalPrice), net:Number(net), totalCost:Number(cost)
                  })
            }
          } else {
            // ไม่กำหนดหมวดหมู่ให้ใช้ 99
            let findCategory = newCategory.find(a=>a.id === 99);
            if(findCategory){
                findCategory.qty += qty
                findCategory.totalPrice += Number(totalPrice)
                findCategory.net += Number(net)
                findCategory.totalCost += Number(cost)
            } else {
                newCategory.push({
                    id:99, name:'ไม่กำหนดหมวดหมู่', qty, totalPrice:Number(totalPrice), net:Number(net), totalCost:Number(cost)
                })
            }
          }

          totalSale += Number(totalPrice)
          totalProfit += Number(net) - Number(cost)
          totalCost += Number(cost)
          totalDiscount += Number(totalPrice)  - Number(net)
          totalQty += qty
          thisPrice += Number(totalPrice)

        });

        // Merge A with firstLevel (Union)
        const mergedMap = new Map();
        // Add all objects from A
        newCategory.forEach(item => mergedMap.set(item.id, item));

        firstLevel.forEach((value, key) => {
            if (!mergedMap.has(key)) {
                mergedMap.set(key, {
                    id:key, 
                    name:value, 
                    qty:0, 
                    totalPrice:0, 
                    net:0, 
                    totalCost:0
                });
            }
        });

        // Convert Map back to an array
        const mergedArray = Array.from(mergedMap.values());
    
        return {
          masterDisplay:normalSort('ยอดขาย',mergedArray.map(item=>{
            const { totalPrice, net, totalCost, ...rest } = item;
            const profit = net - totalCost
            return {
              ...rest,
              totalPrice:handleDigit(totalPrice),
              net:handleDigit(net),
              totalCost:handleDigit(totalCost),
              discount:handleDigit(totalPrice - net),
              profit:handleDigit(profit),
              ratioNet:totalSale>0?handleDigit((net*100/totalSale)):0,
              ratioProfit:totalProfit>0?handleDigit((profit*100/totalProfit)):handleDigit(totalProfit),
              profitPercent:net>0?handleDigit((profit*100/net)):0,
              ยอดขาย:parseFloat(net.toFixed(2))
            }
          })),
          totalSale:handleDigit(totalSale),
          totalProfit:handleDigit(totalProfit),
          totalCost:handleDigit(totalCost),
          totalDiscount:handleDigit(totalDiscount),
          totalQty:handleDigit(totalQty),
          totalPrice:handleDigit(thisPrice),
          totalProfitPercent:totalSale>0?handleDigit(totalProfit*100/totalSale):0
        }
      },[selectedBill, search]);


      const exportToXlsx = () => {
        // Define the headers
        const headers = [
          ["No.", "รายการ", "จำนวนที่ขาย", "ยอดขาย", "ส่วนลด", "ยอดสุทธิ", "ต้นทุน", "กำไร", "อัตรากำไร(%)", "ยอดขายสุทธิต่อทั้งหมด(%)", "กำไรต่อทั้งหมด(%)"]
        ];
      
        // Map the data from the table
        const data = masterDisplay.map((item, index) => [
          index + 1,
          item.name,
          item.qty,
          item.totalPrice,
          item.discount,
          item.net,
          item.totalCost,
          item.profit,
          item.profitPercent,
          item.ratioNet,
          item.ratioProfit
        ]);
      
        // Add the total row with "รวม" spanning two columns
        data.push([
          "รวม", "", // These will be merged
          totalQty,
          totalPrice,
          totalDiscount,
          totalSale,
          totalCost,
          totalProfit,
          totalProfitPercent,
          "100.00",
          "100.00"
        ]);
      
        // Create the worksheet and workbook
        const worksheet = XLSX.utils.aoa_to_sheet([...headers, ...data]);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "ยอดขายตามหมวดหมู่");
      
        // Merge "รวม" over two columns (A12:B12 if it's row 12)
        worksheet["!merges"] = [
          { s: { r: data.length, c: 0 }, e: { r: data.length, c: 1 } } // Merge first two cells of last row
        ];
      
        // Trigger file download
        XLSX.writeFile(workbook, `ยอดขายตามหมวดหมู่ ${stringYMDHMS3(startDate)} - ${stringYMDHMS3(endDate)}.xlsx`);
      };

  return (
    <div  style={styles.container}  >
      <div style={{position:'sticky', top:0, backgroundColor, zIndex:9 }} >
        <h1>ยอดขายตามหมวดหมู่</h1>
        <TimeControl  />
        <SearchAndDownload
            {...{
              placeholder:'ค้นหาด้วยชื่อ',
              search,
              setSearch,
              exportToXlsx
            }}
        />
      </div>
      <Row   style={{ backgroundColor:white,borderRadius:20,marginLeft:0,marginRight:0,marginBottom:20 }} >
          <Col  lg={12} >
            <div style={{ backgroundColor:white,margin:5,padding:10,borderRadius:20,height:'400px',marginRight:0,overflowX:'auto' }} >
                <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center' }} >
                  <h4>ยอดขายรวม: <b>{totalSale}</b> บาท</h4>
                </div>
                <ChartScreen chart={masterDisplay} bar={'ยอดขาย'} name={''} content={'ยอดขาย'} />
            </div>
          </Col>
      </Row>
      <Table striped bordered hover responsive  variant="light"   >
            <thead  >
            <tr>
                <th style={styles.container3}>No.</th>
                <th style={styles.container4}>รายการ</th>
                <th style={styles.container4}>จำนวนที่ขาย</th>
                <th style={styles.container4}>ยอดขาย</th>
                <th style={styles.container4}>ส่วนลด</th>
                <th style={styles.container4}>ยอดสุทธิ</th>
                <th style={styles.container4}>ต้นทุน</th>
                <th style={styles.container4}>กำไร</th>
                <th style={styles.container4}>อัตรากำไร(%)</th>
                <th style={styles.container4}>ยอดขายสุทธิต่อทั้งหมด(%)</th>
                <th style={styles.container4}>กำไรต่อทั้งหมด(%)</th>
            </tr>
            </thead>
            <tbody  >
            {masterDisplay.map((item, index) => {
                const { id, name, totalPrice, discount, totalCost, net, profit, ratioNet, ratioProfit, qty, profitPercent } = item;
                return <tr  style={styles.container5} key={id} >
                            <td style={styles.container6}>{index+1}.</td>
                            <td >{name}</td>
                            <td style={styles.container6}>{qty}</td>
                            <td style={styles.container6}>{totalPrice}</td>
                            <td style={styles.container6}>{discount}</td>
                            <td style={styles.container6}>{net}</td>
                            <td style={styles.container6}>{totalCost}</td>
                            <td style={styles.container6}>{profit}</td>
                            <td style={styles.container6}>{profitPercent}</td>
                            <td style={styles.container6}>{ratioNet}</td>
                            <td style={styles.container6}>{ratioProfit}</td>
                        </tr>
            })}
            <tr>
              <td colSpan={2} style={styles.container6} >รวม</td>
              <td style={styles.container6} >{totalQty}</td>
              <td style={styles.container6} >{totalPrice}</td>
              <td style={styles.container6} >{totalDiscount}</td>
              <td style={styles.container6} >{totalSale}</td>
              <td style={styles.container6} >{totalCost}</td>
              <td style={styles.container6} >{totalProfit}</td>
              <td style={styles.container6} >{totalProfitPercent}</td>
              <td style={styles.container6} >100.00</td>
              <td style={styles.container6} >100.00</td>
            </tr>
            </tbody>
        </Table>
    </div>
  );
}

const styles = {
  container : {
      height:'100vh',
      scrollY:'auto'
  },
  container2 : {
      display:'flex',justifyContent:'flex-end',marginBottom:'1rem'
  },
  container3 : {
      width: '5%', textAlign:'center'
  },
  container4 : {
      width: '15%', textAlign:'center', minWidth:'120px'
  },
  container5 : {
      cursor: 'pointer'
  },
  container6 : {
      textAlign:'center'
  }
};

export default ReportByCategory;